<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

/**
 * The legend for the entry form's keyboard navigation. Two-level navigation is only an improvement if the user knows
 * it is there, so the header carries a permanent affordance rather than leaving Tab's behaviour to be discovered by
 * accident.
 */
const props = defineProps({
  /** Field search is only mounted for some studies — its shortcut is listed only when it is. */
  showSearchShortcut: {
    type: Boolean
  }
});

const rootRef = ref<HTMLElement>();
const open = ref(false);

const isMac = /Mac|iPhone|iPad/.test(navigator.userAgent);

interface Shortcut {
  keys: string[];
  description: string;
}

const shortcuts = computed<Shortcut[]>(() => [
  { keys: ['Tab'], description: 'Next field' },
  { keys: ['Shift', 'Tab'], description: 'Previous field' },
  { keys: ['← / →'], description: 'Move between the inputs of one field' },
  { keys: ['Space'], description: 'Select the focused choice or unit' },
  { keys: ['Tab'], description: 'Next cell, in a table field' },
  { keys: ['Shift', '↑ / ↓'], description: 'Up or down a column, in a table field' },
  ...(props.showSearchShortcut ?
    [{ keys: [isMac ? '⌘' : 'Ctrl', 'K'], description: 'Search for a field by name' }] :
    [])
]);

const onDocumentMouseDown = (event: MouseEvent) => {
  if (!rootRef.value?.contains(event.target as Node)) {
    open.value = false;
  }
};

const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && open.value) {
    event.stopPropagation();
    open.value = false;
  }
};

onMounted(() => {
  document.addEventListener('mousedown', onDocumentMouseDown);
  window.addEventListener('keydown', onKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocumentMouseDown);
  window.removeEventListener('keydown', onKeydown);
});
</script>

<template>
  <div class="shortcuts" ref="rootRef">
    <button type="button"
      class="shortcuts-button"
      :class="{ 'shortcuts-button-open': open }"
      aria-label="Keyboard shortcuts"
      :aria-expanded="open"
      @click="open = !open">
      <FontAwesomeIcon :icon="['fas', 'square-info']" class="shortcuts-icon" />
      <span class="shortcuts-text">Shortcuts</span>
    </button>

    <div class="shortcuts-panel" v-if="open">
      <div class="shortcuts-title">Moving through this form</div>
      <ul class="shortcuts-list">
        <li class="shortcut" v-for="shortcut in shortcuts" :key="shortcut.description">
          <span class="shortcut-keys">
            <template v-for="(key, index) in shortcut.keys" :key="key">
              <span class="shortcut-separator" v-if="index > 0">+</span>
              <kbd>{{ key }}</kbd>
            </template>
          </span>
          <span class="shortcut-description">{{ shortcut.description }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '../../../assets/variables.module' as *;

.shortcuts {
  position: relative;
  flex-shrink: 0;
}
.shortcuts-button {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 10px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: none;
  color: #6b7683;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    background-color: #eef1f4;
    color: #2a3a4b;
  }

  &.shortcuts-button-open {
    background-color: #fff;
    border-color: #ced3d9;
    color: #2a3a4b;
  }
}
.shortcuts-icon {
  font-size: 14px;
}
.shortcuts-panel {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 10;
  width: 330px;
  padding: 12px 14px;
  background-color: #fff;
  border: 1px solid #dfe3e8;
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(15, 15, 15, 0.12);
}
.shortcuts-title {
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 700;
  color: #2a3a4b;
}
.shortcuts-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.shortcut {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 4px 0;
  font-size: 13px;
  color: #575e66;
}
.shortcut-keys {
  flex: 0 0 108px;
  display: flex;
  align-items: center;
  gap: 3px;

  kbd {
    padding: 1px 6px;
    border: 1px solid #dfe3e8;
    border-bottom-width: 2px;
    border-radius: 4px;
    background-color: #f8f9fa;
    color: #2a3a4b;
    font-family: inherit;
    font-size: 11px;
    line-height: 16px;
    white-space: nowrap;
  }
}
.shortcut-separator {
  font-size: 10px;
  color: #8a939e;
}
.shortcut-description {
  flex: 1;
}

@media (max-width: 600px) {
  // A touch keyboard has none of these keys to press.
  .shortcuts {
    display: none;
  }
}
</style>
